// 单跳转组卡片 — 组头(名称+状态徽章+删除组)+ 展开/折叠 + 落地方式 radio + 三级卡片。
import { useState } from 'react'
import { ChevronDown, ChevronRight, Activity, Trash2 } from 'lucide-react'
import { useToast } from '@dogdisk/shared'
import {
  useCreateJumpDomain,
  useUpdateChain,
  useDeleteChain,
  useDeleteDomain,
  useHealthCheckChain,
  type ChainLandingType,
  type JumpChainWithDomains,
  type JumpDomainRow,
} from '../../_lib/jumpdomain-api'
import { LevelCard } from './level-card'
import { LandingProxyPreview } from './landing-proxy-preview'
import { ConfirmDialog } from './confirm-dialog'
import { AddDomainDialog } from './add-chain-dialog'

function LandingRadio({
  checked,
  label,
  testid,
  disabled,
  onSelect,
}: {
  checked: boolean
  label: string
  testid: string
  disabled?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      data-testid={testid}
      aria-pressed={checked}
      className={[
        'rounded-full border px-3 py-1 text-sm disabled:opacity-50',
        checked
          ? 'border-amber-500 bg-amber-50 font-medium text-amber-700'
          : 'border-gray-300 text-gray-500 hover:bg-gray-50',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

type StatusBadge = { label: string; cls: string }

const STATUS_BADGE_ACTIVE: StatusBadge = { label: '运行中', cls: 'bg-green-100 text-green-700' }

const STATUS_BADGE: Record<string, StatusBadge> = {
  active: STATUS_BADGE_ACTIVE,
  disabled: { label: '已禁用', cls: 'bg-gray-100 text-gray-500' },
}

export function ChainCard({ chain }: { chain: JumpChainWithDomains }) {
  const [expanded, setExpanded] = useState(true)
  const [addDomainLevel, setAddDomainLevel] = useState<1 | 2 | 3 | null>(null)
  const [confirmDeleteChain, setConfirmDeleteChain] = useState(false)
  const [pendingDeleteDomain, setPendingDeleteDomain] = useState<JumpDomainRow | null>(null)

  const createDomain = useCreateJumpDomain()
  const updateChain = useUpdateChain()
  const deleteChain = useDeleteChain()
  const deleteDomain = useDeleteDomain()
  const healthCheck = useHealthCheckChain()
  const toast = useToast()

  const onHealthCheck = async () => {
    try {
      const r = await healthCheck.mutateAsync(chain.id)
      if (r.checked === 0) {
        toast.push('该组暂无可测试的 L1/L2 域名', { variant: 'info' })
      } else {
        toast.push(`测试完成:正常 ${r.up} / 异常 ${r.down}`, {
          variant: r.down === 0 ? 'success' : 'error',
        })
      }
    } catch {
      toast.push('测试失败', { variant: 'error' })
    }
  }

  const isCustom = chain.landing_type === 'custom'
  const badge = STATUS_BADGE[chain.status] ?? STATUS_BADGE_ACTIVE

  const onSetLanding = async (lt: ChainLandingType) => {
    if (lt === chain.landing_type) return
    try {
      await updateChain.mutateAsync({ id: chain.id, landing_type: lt })
    } catch {
      toast.push('切换落地方式失败', { variant: 'error' })
    }
  }

  const levels: (1 | 2 | 3)[] = isCustom ? [1, 2, 3] : [1, 2]

  return (
    <div
      className="rounded-lg border border-gray-200"
      data-testid={`Ye71I-chain-${chain.id}`}
      data-landing-type={chain.landing_type}
    >
      <div className="flex w-full items-center justify-between px-4 py-3">
        <button
          type="button"
          className="flex items-center gap-2 font-medium"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`Ye71I-toggle-${chain.id}`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          {chain.name}
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${badge.cls}`}>
            {badge.label}
          </span>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
            {isCustom ? '自定义域名' : '本站代理'}
          </span>
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onHealthCheck}
            disabled={healthCheck.isPending}
            className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50"
            title="一键测试组内 L1/L2 域名探活"
            data-testid={`Ye71I-health-check-${chain.id}`}
          >
            <Activity className="h-3.5 w-3.5" />
            {healthCheck.isPending ? '测试中…' : '一键测试'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmDeleteChain(true)}
            className="text-gray-400 hover:text-red-600"
            title="删除跳转组"
            data-testid={`Ye71I-delete-chain-${chain.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-gray-100 p-4">
          <div className="flex items-center gap-3" data-testid={`Ye71I-landing-${chain.id}`}>
            <span className="text-sm text-gray-500">落地方式：</span>
            <LandingRadio
              checked={!isCustom}
              label="本站代理"
              testid={`Ye71I-landing-site_agent-${chain.id}`}
              disabled={updateChain.isPending}
              onSelect={() => onSetLanding('site_agent')}
            />
            <LandingRadio
              checked={isCustom}
              label="自定义域名"
              testid={`Ye71I-landing-custom-${chain.id}`}
              disabled={updateChain.isPending}
              onSelect={() => onSetLanding('custom')}
            />
          </div>

          {levels.map((lv) => (
            <LevelCard
              key={lv}
              chainId={chain.id}
              level={lv}
              domains={chain.domains.filter((d) => d.level === lv)}
              onAddSingle={() => setAddDomainLevel(lv)}
              onDeleteDomain={(row) => setPendingDeleteDomain(row)}
            />
          ))}

          {!isCustom && <LandingProxyPreview />}
        </div>
      )}

      {addDomainLevel !== null && (
        <AddDomainDialog
          chainId={chain.id}
          level={addDomainLevel}
          onClose={() => setAddDomainLevel(null)}
          onSubmit={async (input) => {
            await createDomain.mutateAsync(input)
            setAddDomainLevel(null)
          }}
        />
      )}

      {confirmDeleteChain && (
        <ConfirmDialog
          title="删除跳转组"
          message={`确认删除「${chain.name}」?组内所有域名将一并删除,不可恢复。`}
          busy={deleteChain.isPending}
          testid={`Ye71I-confirm-delete-chain-${chain.id}`}
          onCancel={() => setConfirmDeleteChain(false)}
          onConfirm={async () => {
            try {
              await deleteChain.mutateAsync(chain.id)
              toast.push('已删除跳转组', { variant: 'success' })
            } catch {
              toast.push('删除失败', { variant: 'error' })
            } finally {
              setConfirmDeleteChain(false)
            }
          }}
        />
      )}

      {pendingDeleteDomain && (
        <ConfirmDialog
          title="删除域名"
          message={`确认删除域名「${pendingDeleteDomain.domain}」?`}
          busy={deleteDomain.isPending}
          testid={`Ye71I-confirm-delete-domain-${pendingDeleteDomain.id}`}
          onCancel={() => setPendingDeleteDomain(null)}
          onConfirm={async () => {
            try {
              await deleteDomain.mutateAsync(pendingDeleteDomain.id)
              toast.push('已删除域名', { variant: 'success' })
            } catch {
              toast.push('删除失败', { variant: 'error' })
            } finally {
              setPendingDeleteDomain(null)
            }
          }}
        />
      )}
    </div>
  )
}
